import type { i18n as I18nInstance, ResourceKey } from 'i18next';

import { DEFAULT_TENANT } from '@/constants/tenant';

import { NAME_SPACES, SupportedLanguages } from '@/i18n/i18n';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';

import { deepMerge } from '@/utils/deepMerge';

async function importBaseTranslationResource(
  language: Language,
  namespaces = NAME_SPACES,
): Promise<Record<string, ResourceKey>> {
  const resources: Record<string, ResourceKey> = {};
  for (const namespace of namespaces) {
    try {
      const module = await import(
        `../i18n/locales/default/${language}/${namespace}.json`
      );
      resources[namespace] = module.default;
    } catch (e) {
      console.warn(
        `Could not load ${namespace} for ${DEFAULT_TENANT} tenant in language ${language}: `,
        e,
      );
    }
  }
  return resources;
}

export async function importTenantTranslationResource(
  tenant: Tenant,
  language: Language,
  namespaces = NAME_SPACES,
): Promise<Record<string, ResourceKey>> {
  const resources: Record<string, ResourceKey> = {};
  for (const namespace of namespaces) {
    try {
      const module = await import(
        `../i18n/locales/tenants/${tenant}/${language}/${namespace}.json`
      );

      resources[namespace] = module.default;
    } catch (e) {
      console.warn(
        `Could not load ${namespace} for tenant ${tenant} in language ${language}: `,
        e,
      );
    }
  }
  return resources;
}

export async function loadTenantTranslations(
  tenant: Tenant = DEFAULT_TENANT,
  language = Language.English,
): Promise<Record<string, ResourceKey>> {
  let baseResource: Record<string, ResourceKey> = {};
  let tenantResource: Record<string, ResourceKey> = {};
  try {
    baseResource = await importBaseTranslationResource(language);
  } catch {
    baseResource = {};
  }

  try {
    tenantResource = await importTenantTranslationResource(tenant, language);
  } catch {
    tenantResource = {};
  }

  const mergedTranslations = deepMerge(baseResource, tenantResource);
  return mergedTranslations;
}

const supportedLanguageCodes: readonly string[] = SupportedLanguages;

export function isSupportedLanguage(lang: unknown): lang is Language {
  return typeof lang === 'string' && supportedLanguageCodes.includes(lang);
}

export async function applyMergedTenantResources(
  i18nInstance: I18nInstance,
  tenant: Tenant,
  language: Language,
): Promise<void> {
  const resources = await loadTenantTranslations(tenant, language);
  for (const namespace of Object.keys(resources)) {
    i18nInstance.addResourceBundle(language, namespace, resources[namespace]);
  }
  if (i18nInstance.language !== language) {
    await i18nInstance.changeLanguage(language);
  }
}
